import { request, requestWithMsg, MsgResult, PageResult } from './request';

// 站点数据同步 (美服→国服镜像, 2026-08-17).
// 后端: material-library internal/app/datasync (/v1/sync/*).
// master(美服) 上 overview 返回 siteRole=master 空态; replica(国服) 返回全量状态.

export interface SyncOverviewTable {
    table: string;
    order: number;
    status: 'idle' | 'running' | 'failed' | 'paused' | string;
    /** 已确认源端水位 (unix 秒) */
    watermark: number;
    /** 源端 serverTime - watermark; -1 = 未知 */
    lagSeconds: number;
    localRows: number;
    /** -1 = 源端不可达 */
    sourceRows: number;
    lastSyncAt: number;
    lastOkAt: number;
    lastReconcileAt: number;
    failStreak: number;
    lastError: string;
}

export interface SyncOverview {
    siteRole: 'master' | 'replica' | string;
    imageMode?: 'passthrough' | 'rewrite' | string;
    serverTime?: number;
    sourceError?: string;
    pendingImages?: number;
    tables?: SyncOverviewTable[];
}

export interface SyncLog {
    id: number;
    tableName: string;
    kind: 'incr' | 'reconcile' | 'manual' | string;
    since: number;
    watermarkTo: number;
    createdCnt: number;
    updatedCnt: number;
    deletedCnt: number;
    imageCnt: number;
    status: 'ok' | 'failed' | 'partial' | string;
    error: string;
    costMs: number;
    createdAt: number;
}

export interface SyncRecord {
    id: number;
    batchId: number;
    tableName: string;
    rowId: number;
    action: 'created' | 'updated' | 'deleted' | string;
    /** 美服源行 JSON (列表接口不返回, 详情才有) */
    sourceData?: string;
    /** 国服落库行 JSON (列表接口不返回, 详情才有) */
    targetData?: string;
    imageStatus: 'none' | 'done' | 'pending' | 'failed' | string;
    retryCount: number;
    nextRetryAt: number;
    error: string;
    createdAt: number;
}

export interface RowDiff {
    table: string;
    id: number;
    localExists: boolean;
    localUpdatedAt?: string;
    localDeleted?: boolean;
    sourceExists?: boolean;
    sourceUpdatedAt?: string;
    sourceDeleted?: boolean;
    sourceError?: string;
}

const BASE = '/v1/sync';

export function fetchSyncOverview(): Promise<SyncOverview> {
    return request(`${BASE}/overview`);
}

export function listSyncLogs(params: {
    table?: string;
    kind?: string;
    status?: string;
    page?: number;
    pageSize?: number;
}): Promise<PageResult<SyncLog>> {
    return request(`${BASE}/logs`, { query: params });
}

export function listSyncRecords(params: {
    batchId?: number;
    table?: string;
    rowId?: number;
    action?: string;
    imageStatus?: string;
    page?: number;
    pageSize?: number;
}): Promise<PageResult<SyncRecord>> {
    return request(`${BASE}/records`, { query: params });
}

export function getSyncRecord(id: number): Promise<SyncRecord> {
    return request(`${BASE}/records/${id}`);
}

export function fetchRowDiff(table: string, id: number): Promise<RowDiff> {
    return request(`${BASE}/row-diff`, { query: { table, id } });
}

// ---- 运行时配置 (页面修改, 下一轮同步生效, 免重启) ----

export interface SyncSettings {
    /** 增量轮询间隔秒 (10~3600) */
    intervalSeconds: number;
    /** 每页拉取行数 (50~1000) */
    pageSize: number;
    /** passthrough=图片直通原链接; rewrite=搬图到国服存储并改写 */
    imageMode: 'passthrough' | 'rewrite' | string;
    /** sync_record 保留天数 (7~3650) */
    recordRetainDays: number;
    /**
     * 待搬运图片的源域名白名单 (逗号分隔). 配漏 = 搬图静默不生效,
     * 美服图片散落在 storage-us.atomm.com / image-res.xtool.com 等多个域名.
     */
    imageHosts: string;
    /** true=页面设置生效; false=仍在用环境变量默认 */
    fromDb: boolean;
}

export function fetchSyncSettings(): Promise<SyncSettings> {
    return request(`${BASE}/settings`);
}

/** admin; 保存后下一轮同步生效 */
export function updateSyncSettings(s: SyncSettings): Promise<MsgResult<SyncSettings>> {
    return requestWithMsg(`${BASE}/settings`, { method: 'PUT', body: s });
}

// ---- 以下操作类接口需 admin ----

export function triggerTableSync(table: string): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/tables/${table}/trigger`, { method: 'POST' });
}

export function reconcileTable(table: string): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/tables/${table}/reconcile`, { method: 'POST' });
}

export function pauseTableSync(table: string): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/tables/${table}/pause`, { method: 'POST' });
}

export function resumeTableSync(table: string): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/tables/${table}/resume`, { method: 'POST' });
}

export function retrySyncImage(recordId: number): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/records/${recordId}/retry-image`, { method: 'POST' });
}

// ---- 全量校验兜底 (2026-08-19) ----
// 定时同步只看 updated_at 水位、对账只比 id 集合; 本操作逐行全字段比对两端,
// 以美服为准修复国服, 图片按"有图不改、缺图从美服搬到国服存储"处理.

export interface VerifyOptions {
    /** 限定表名; 空 = 全部同步白名单表 */
    tables?: string[];
    /** true = 只报差异, 不写库不搬图 */
    dryRun?: boolean;
    /** true = 缺图时从美服下载并上传到国服存储, 再改写链接 */
    migrateImages?: boolean;
    /** 本次搬图上限 (0 = 后端默认 500) */
    imageBudget?: number;
}

export type VerifyTableStatus = 'pending' | 'running' | 'done' | 'failed' | 'canceled' | string;

export interface VerifyTableProgress {
    table: string;
    /** 源端已扫行数 */
    sourceRows: number;
    checked: number;
    /** 字段有差异的行数 (含本地缺失) */
    mismatched: number;
    /** 已按美服重写的行数 */
    fixed: number;
    missingLocal: number;
    /** 源端已无、国服多余被软删的行数 */
    extraLocal: number;
    imagesMigrated: number;
    /** 因预算用尽/搬图失败/未勾选搬图而没补上的图片数 */
    imagesPending: number;
    status: VerifyTableStatus;
    error: string;
    costMs: number;
}

export interface VerifyProgress {
    running: boolean;
    canceled: boolean;
    startedAt: number;
    endedAt: number;
    options: VerifyOptions;
    /** 剩余搬图预算; 为 0 且有 imagesPending = 预算用尽, 需再跑一轮 */
    imageBudgetLeft: number;
    tables: VerifyTableProgress[];
    totals: VerifyTableProgress;
    error: string;
    triggeredBy: number;
    /** master 站访问时后端只回站点角色 */
    siteRole?: string;
}

/** admin; 立即返回, 后台执行, 用 fetchVerifyProgress 轮询 */
export function startVerify(opts: VerifyOptions): Promise<MsgResult<VerifyProgress>> {
    return requestWithMsg(`${BASE}/verify`, { method: 'POST', body: opts });
}

export function fetchVerifyProgress(): Promise<VerifyProgress> {
    return request(`${BASE}/verify`);
}

/** admin; 当前表处理完即停, 已修的行不回滚 */
export function cancelVerify(): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/verify/cancel`, { method: 'POST' });
}
